<template>
  <v-container class="pt-0" fluid>
    <v-row class="align-center">
      <v-col cols="auto">
        <v-card-title class="pt-0 pl-0">
          Content Types
        </v-card-title>
      </v-col>
      <v-spacer />
      <v-col cols="auto" style="min-width: 200px;">
        <v-text-field
          v-model="searchName"
          label="Search by name"
          clearable
          hide-details
          dense
          outlined
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>
      <v-col cols="auto" style="min-width: 150px;">
        <v-select
          v-model="filterKind"
          :items="kindOptions"
          label="Kind"
          clearable
          hide-details
          dense
          outlined
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          :loading="isLoading"
          @click="onAdd"
        >
          Add Type
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pt-0">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="filteredItems"
            :loading="isLoading"
            item-key="type_id"
            :sort-by="['created']"
            :sort-desc="[true]"
            :items-per-page="30"
            :footer-props="{ 'items-per-page-options': [30, 50, 100] }"
            fixed-header
            :height="tableHeight"
          >
            <template #item.kind="{ value }">
              <v-chip :color="value === 'fragment' ? 'purple' : 'blue'" small outlined>
                {{ value || 'content' }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <v-icon class="mr-2" @click="onEdit(item)">
                mdi-pencil
              </v-icon>
              <v-icon class="mr-2" @click="onEditSchema(item)">
                mdi-code-json
              </v-icon>
              <v-icon color="error" @click="onDelete(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <drawer-dialog
      v-model="isEditing"
      width="50%"
      max-width="800px"
    >
      <cms-type-form
        v-if="isEditing"
        :item="editingTarget"
        :is-loading="isLoading"
        @close="isEditing = false"
        @save="onSave"
      />
    </drawer-dialog>

    <drawer-dialog
      v-model="isSchemaEditing"
      width="80%"
      max-width="1400px"
    >
      <cms-type-schema-form
        v-if="isSchemaEditing"
        :type="schemaTarget"
        :is-loading="isLoading"
        @close="isSchemaEditing = false"
        @save="onSaveSchema"
      />
    </drawer-dialog>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import { DrawerDialog } from '@/components/core/DrawerDialog'
import CmsTypeForm from './CmsTypeForm'
import CmsTypeSchemaForm from './CmsTypeSchemaForm'

export default {
  components: {
    DrawerDialog,
    CmsTypeForm,
    CmsTypeSchemaForm,
  },
  data() {
    return {
      isLoading: false,
      items: [],
      editingTarget: null,
      schemaTarget: null,
      searchName: '',
      filterKind: null,
      headers: [
        { text: 'ID', value: 'type_id', width: 80 },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        { text: 'Kind', value: 'kind', width: 100 },
        { text: 'Description', value: 'description' },
        { text: 'Created', value: 'created' },
        { text: 'Actions', value: 'actions', sortable: false, width: 150 },
      ],
    }
  },
  computed: {
    tableHeight() {
      return 'calc(100vh - 155px)'
    },
    filteredItems() {
      let result = this.items
      if (this.searchName) {
        const q = this.searchName.toLowerCase()
        result = result.filter(t => (t.name || '').toLowerCase().includes(q))
      }
      if (this.filterKind) {
        result = result.filter(t => (t.kind || 'content') === this.filterKind)
      }
      return result
    },
    kindOptions() {
      const kinds = new Set(this.items.map(t => t.kind || 'content'))
      return [...kinds].sort()
    },
    isEditing: {
      get() {
        return !!this.editingTarget
      },
      set(value) {
        if (!value) {
          this.editingTarget = null
        }
      },
    },
    isSchemaEditing: {
      get() {
        return !!this.schemaTarget
      },
      set(value) {
        if (!value) {
          this.schemaTarget = null
        }
      },
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.isLoading = true
      return API.call('/cms/type/list.json', {})
        .then(r => {
          this.items = r.data.items || []
        })
        .finally(() => this.isLoading = false)
    },
    onAdd() {
      this.editingTarget = { slug: '', name: '', description: '', kind: 'content' }
    },
    onEdit(item) {
      this.editingTarget = { ...item }
    },
    onEditSchema(item) {
      this.schemaTarget = { ...item }
    },
    onSave(item) {
      this.isLoading = true
      return API.call('/cms/type/upsert.json', item)
        .then(() => {
          this.$showSuccess('Type saved')
          this.editingTarget = null
          this.loadData()
        })
        .finally(() => this.isLoading = false)
    },
    onSaveSchema(data) {
      this.isLoading = true
      return API.call('/cms/type-schema/upsert.json', data)
        .then(() => {
          this.$showSuccess('Schema saved')
          this.schemaTarget = null
        })
        .finally(() => this.isLoading = false)
    },
    onDelete(item) {
      this.$root.$confirm('Confirm', `Delete type "${item.name}"?`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isLoading = true
          API.call('/cms/type/delete.json', { type_id: item.type_id })
            .then(() => {
              this.$showSuccess('Type deleted')
              this.loadData()
            })
            .finally(() => this.isLoading = false)
        })
    },
  },
}
</script>
